import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { signOut } from 'firebase/auth';
import './ProfileChoice.css';

function ProfileChoice({ onProfileSelect }) {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [showPinModal, setShowPinModal] = useState(false);
  const [error, setError] = useState('');
  const user = auth.currentUser;

  // Load profiles from Users document
  useEffect(() => {
    if (!user) return;
    const usersDocRef = doc(db, 'users', user.uid, 'Profiles', 'Users');
    const unsubscribe = onSnapshot(usersDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        console.log('Raw Firestore data:', data);
        
        // Check if data.users is an array
        if (data.users && Array.isArray(data.users)) {
          const profilesArray = data.users.map((profile, index) => ({
            id: index.toString(),
            ...profile
          }));
          console.log('Profiles array:', profilesArray);
          setProfiles(profilesArray);
        } else {
          // Fallback to old structure
          const profilesArray = Object.keys(data).map(key => ({
            id: key,
            ...data[key]
          }));
          console.log('Profiles array (fallback):', profilesArray);
          setProfiles(profilesArray);
        }
      } else {
        console.log('Users document does not exist!');
        setProfiles([]);
      }
    });
    return unsubscribe;
  }, [user]);

  const handleProfileClick = (profile) => {
    if (profile.pin) {
      // Profile has PIN - show PIN modal
      setSelectedProfile(profile);
      setShowPinModal(true);
      setPinInput('');
      setError('');
    } else {
      // No PIN - go directly to dashboard
      onProfileSelect(profile);
    }
  };

  const handlePinSubmit = () => {
    if (pinInput === selectedProfile.pin) {
      // Correct PIN
      setShowPinModal(false);
      onProfileSelect(selectedProfile);
    } else {
      // Wrong PIN
      setError('Incorrect PIN');
      setPinInput('');
    }
  };

  const handlePinCancel = () => {
    setShowPinModal(false);
    setSelectedProfile(null);
    setPinInput('');
    setError('');
  };

  const handleLogout = () => {
    signOut(auth);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return parts[0][0] + parts[1][0];
    }
    return name.substring(0, 2).toUpperCase();
  };

  console.log('ProfileChoice rendering, profiles:', profiles);

  return (
    <div className="profile-choice-container">
      <div className="profile-content">
        <h1>Who's using Vault?</h1>
        
        {profiles.length === 0 ? (
          // No profiles found
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <div style={{ 
              background: 'rgba(255,255,255,0.9)', 
              borderRadius: '16px', 
              padding: '40px', 
              maxWidth: '500px', 
              margin: '0 auto 40px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
            }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
              <h2 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '24px' }}>Account Not Set Up</h2>
              <p style={{ margin: '0', color: '#666', fontSize: '16px', lineHeight: '1.6' }}>
                This account doesn't have any profiles configured yet. 
                Please set up your profile in the Vault mobile app first.
              </p>
            </div>
            <button className="logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        ) : (
          // Profiles found
          <>
            <div className="profiles-horizontal">
              {profiles.map(profile => (
                <div 
                  key={profile.id} 
                  className="profile-card"
                  onClick={() => handleProfileClick(profile)}
                >
                  <div className={`avatar ${profile.type?.toLowerCase()}`}>
                    {getInitials(profile.name)}
                    {profile.pin && <div className="lock-badge">🔒</div>}
                  </div>
                  <div className="profile-name">{profile.name}</div>
                  <div className="profile-type">{profile.type}</div>
                </div>
              ))}
            </div>

            <button className="logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </>
        )}
      </div>

      {/* PIN Modal */}
      {showPinModal && (
        <div className="pin-modal-overlay" onClick={handlePinCancel}>
          <div className="pin-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`avatar ${selectedProfile?.type?.toLowerCase()}`}>
              {getInitials(selectedProfile?.name)}
            </div>
            <h3>Enter PIN</h3>
            <p>{selectedProfile?.name}'s profile is protected</p>
            
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="4"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter PIN"
              autoFocus
              style={{
                width: '100%',
                padding: '15px',
                fontSize: '24px',
                textAlign: 'center',
                border: '2px solid #ddd',
                borderRadius: '8px',
                marginBottom: '20px',
                letterSpacing: '10px'
              }}
            />

            {error && <p className="error-message">{error}</p>}
            
            <div className="pin-buttons">
              <button className="pin-btn cancel" onClick={handlePinCancel}>Cancel</button>
              <button 
                className="pin-btn submit" 
                onClick={handlePinSubmit}
                disabled={pinInput.length !== 4}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileChoice;